import { useAdminStore } from "@/stores/admin-store";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

/**
 * The admin app has two context modes: Global Administration and a selected
 * client ("Managing client"). Client mode must be unmistakable (founder,
 * 2026-09-14): a coloured sidebar, a banner on every client page, and the
 * page always matching the mode.
 *
 * The accent is a DEEP MUTED TEAL (founder, 2026-09-14: the first, brighter
 * teal-700 was too loud) — every client-mode colour comes from
 * `CLIENT_ACCENT` so trying another colour is a one-place change (Tailwind
 * needs full class names, so swap the whole strings, e.g. teal → indigo).
 */
export const CLIENT_ACCENT = {
	/** Solid band behind "Managing client" in the sidebar and the page banner. */
	band: "bg-teal-900",
	bandText: "text-white",
	bandMuted: "text-teal-200/80",
	/** Small pill on the band (industry). */
	bandChip: "bg-teal-950/60",
	/** Sidebar right edge while in client mode. */
	sidebarEdge: "border-r-2 border-teal-700",
	/** Selected sidebar item. */
	navActive: "bg-teal-800/40 text-white border-l-2 border-teal-400/80",
	navHover: "hover:bg-teal-900/50 hover:text-white",
	sectionLabel: "text-teal-300/60",
	/** Buttons on the banner. */
	bannerButton:
		"border-teal-600/60 bg-teal-950/40 text-teal-50 hover:bg-teal-800 hover:text-white",
	/** Links and hover borders on client pages (light and dark themes). */
	link: "text-teal-800 hover:underline dark:text-teal-300",
	hoverBorder: "hover:border-teal-600/60",
	/** "Current" marker in the Switch client list. */
	currentMark: "text-teal-800 dark:text-teal-300",
} as const;

/** `/client` and `/client/...` are client-context pages; `/clients` is global. */
export function isClientRoute(pathname: string): boolean {
	return pathname === "/client" || pathname.startsWith("/client/");
}

/** Pages that belong to neither mode and must not change it. */
export function isNeutralRoute(pathname: string): boolean {
	return pathname.startsWith("/access-denied");
}

export const CLIENT_HOME = "/client/dashboard";
export const GLOBAL_CLIENTS_PAGE = "/clients";

/**
 * Enter / switch / exit client mode together with the navigation that keeps
 * the page in step with the mode.
 */
export function useClientContextNavigation() {
	const navigate = useNavigate();
	const selectClient = useAdminStore((s) => s.selectClient);
	const clearClientContext = useAdminStore((s) => s.clearClientContext);

	/** Select a client and open its dashboard (or `to`, e.g. onboarding). */
	const enterClient = useCallback(
		(id: string, name: string, to: string = CLIENT_HOME) => {
			selectClient(id, name);
			navigate({ to });
		},
		[navigate, selectClient],
	);

	/** Leave client mode and return to the global Clients list. */
	const exitClient = useCallback(() => {
		clearClientContext();
		navigate({ to: GLOBAL_CLIENTS_PAGE });
	}, [clearClientContext, navigate]);

	return { enterClient, exitClient };
}
